import { FC, ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { useMutation } from '@apollo/client';
import Toolbar from 'components/common/Toolbar/Toolbar';
import Input from 'components/common/Input/Input';
import ELEMENT from 'common/element';
import './styles.scss';
import { UPDATE_PARSER_SAMPLE } from '../../queries';

export type LogObject =
  | {
      index: number | null;
      log: string | null;
    }
  | undefined;

type LogSample = {
  parser: string;
  onSelectedLogChange: (logLine: LogObject) => void;
  activeLogLine: LogObject;
  onParserSampleChanged: (data: Array<string>) => void;
  className?: string;
};

const LogSample: FC<LogSample> = ({
  parser,
  activeLogLine,
  onSelectedLogChange,
  onParserSampleChanged,
  className,
}: LogSample) => {
  const [updateParserSample, { data, loading }] = useMutation(UPDATE_PARSER_SAMPLE, {
    onCompleted: (parserSampleData) => {
      onParserSampleChanged(parserSampleData.updateParserSample?.logSample || []);
    },
  });
  const [parserSampleInputValue, setParserSampleInputValue] = useState('');
  const onSetInputValue = (e: SyntheticEvent): void => {
    setParserSampleInputValue((e.target as HTMLInputElement).value);
  };

  useEffect(() => {
    if (parser) {
      updateParserSample({
        variables: {
          id: parser,
          filter: '',
        },
      });
    }
  }, [parser, updateParserSample]);

  const onUpdateLogSubmit = (): void => {
    updateParserSample({
      variables: {
        filter: parserSampleInputValue,
        id: parser,
      },
    });
  };

  const onLogClick = (log: LogObject): void => {
    onSelectedLogChange(log?.index === activeLogLine?.index ? undefined : log);
  };
  const renderLogSample = (): ReactNode => {
    if (loading) {
      return (
        <div data-test-id={ELEMENT.logSampleContainer} className="ParserPage__LogSampleContainer">
          <div className="ParserPage__LogSampleContainer ParserPage__NoContentMessage">Log sample is loading...</div>
        </div>
      );
    }
    return (
      <div data-test-id={ELEMENT.logSampleContainer} className="ParserPage__LogSampleContainer">
        {data?.updateParserSample?.logSample?.length > 0 ? (
          data?.updateParserSample?.logSample?.map((log: string, index: number) => (
            <div
              className={`ParserPage__LogLine ${activeLogLine?.index === index ? 'ParserPage__LogLine--Active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={(): void => {
                onLogClick({ log, index });
              }}
              key={'log-sample-' + index}
              data-test-id={ELEMENT.parserLogLine}
            >
              {log}
            </div>
          ))
        ) : (
          <div className="ParserPage__NoContentMessage" data-test-id="">
            No recent log messages for this parser
          </div>
        )}
      </div>
    );
  };

  const leftItems = [
    <div className="SectionHeader" data-test-id={ELEMENT.logSampleHeader}>
      Log Sample
    </div>,
  ];
  const rightItems = [
    <div>Filter</div>,
    <Input
      testId={ELEMENT.logSampleInput}
      onChange={onSetInputValue}
      onPressEnter={onUpdateLogSubmit}
      value={parserSampleInputValue}
      labelClassName="ParserPage__UpdateSampleInputLabel"
      size="sm"
    />,
    <Button variant={ButtonVariant.SECONDARY} size="sm" onClick={onUpdateLogSubmit} testId={ELEMENT.updateSampleButton}>
      Update Sample
    </Button>,
  ];
  return (
    <div className={className}>
      <Toolbar leftItems={leftItems} rightItems={rightItems} />
      {renderLogSample()}
      <div className="ParserPage__TipContainer">
        {activeLogLine
          ? 'Line selected - Click again to unselect'
          : 'Click on a line to see details of parser evaluation'}
      </div>
    </div>
  );
};

export default LogSample;
